import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useSandboxPreview } from "@/contexts/SandboxPreviewContext";
import { useAuthContext } from "@/contexts/AuthContext";
import { clearFeatureFlagCache } from "@/hooks/useFeatureFlag";
import { FlaskConical, Rocket, RotateCcw, ExternalLink, Eye, X, Download, Upload } from "lucide-react";


interface FeatureFlag {
  id: string;
  key: string;
  name: string;
  description: string | null;
  target_area: string | null;
  status: "sandbox" | "live" | "rolled_back";
  enabled_for_roles: string[];
  enabled_for_plans: string[];
  released_at: string | null;
  notes: string | null;
}

interface TestMember {
  id: string;
  full_name: string;
  email: string;
  role: string | null;
}

const ROLE_OPTIONS = [
  "Parent",
  "Chair",
  "Treasurer",
  "Secretary",
  "Trustee",
  "Catering",
  "Communications",
  "School Liaison",
  "Assets",
  "Community",
  "Shop",
  "Events",
];

interface QuickLink {
  label: string;
  role: string;
  build: (ptaId: string) => string;
}

const TEST_PTA_QUICK_LINKS: QuickLink[] = [
  { label: "Public Fundraising Page", role: "Parent", build: (id) => `/fundraising/${id}` },
  { label: "Parent Portal", role: "Parent", build: (id) => `/portal/${id}` },
  { label: "PTA Admin", role: "Chair", build: () => "/pta-admin" },
  { label: "Chair Dashboard", role: "Chair", build: () => "/dashboard/chair" },
  { label: "Treasurer Dashboard", role: "Treasurer", build: () => "/dashboard/treasurer" },
  { label: "Secretary Dashboard", role: "Secretary", build: () => "/dashboard/secretary" },
  { label: "Trustee Dashboard", role: "Trustee", build: () => "/dashboard/trustee" },
  { label: "Events Dashboard", role: "Events", build: () => "/dashboard/events" },
  { label: "Shop Dashboard", role: "Shop", build: () => "/dashboard/shop" },
  { label: "Catering Dashboard", role: "Catering", build: () => "/dashboard/catering" },
  { label: "Communications", role: "Communications", build: () => "/dashboard/communications" },
  { label: "School Liaison", role: "School Liaison", build: () => "/dashboard/school-liaison" },
  { label: "Assets Dashboard", role: "Assets", build: () => "/dashboard/assets" },
  { label: "Community Dashboard", role: "Community", build: () => "/dashboard/community" },
];

export default function SandboxTab() {
  const { toast } = useToast();
  const { user } = useAuthContext();
  const { preview, startPreview, exitPreview } = useSandboxPreview();
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [testPta, setTestPta] = useState<{ id: string; name: string } | null>(null);
  const [testMembers, setTestMembers] = useState<TestMember[]>([]);
  const [testParent, setTestParent] = useState<{ id: string; full_name: string; email: string } | null>(null);
  const [previewFor, setPreviewFor] = useState<FeatureFlag | null>(null);
  const [previewRole, setPreviewRole] = useState("Chair");

  const load = useCallback(async () => {
    const { data: flagsData } = await supabase
      .from("feature_flags")
      .select("*")
      .order("created_at", { ascending: false });
    setFlags((flagsData ?? []) as FeatureFlag[]);

    const { data: pta } = await supabase
      .from("ptas")
      .select("id,name")
      .eq("is_test_pta", true)
      .maybeSingle();
    if (pta) {
      setTestPta(pta);
      const { data: mems } = await supabase
        .from("members")
        .select("id,full_name,email,role")
        .eq("pta_id", pta.id)
        .eq("is_test_account", true)
        .order("role");
      setTestMembers((mems ?? []) as TestMember[]);
      const { data: par } = await supabase
        .from("parents")
        .select("id,full_name,email")
        .eq("pta_id", pta.id)
        .eq("is_test_account", true)
        .maybeSingle();
      if (par) setTestParent(par);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sandboxFlags = useMemo(() => flags.filter((f) => f.status !== "live"), [flags]);
  const liveFlags = useMemo(() => flags.filter((f) => f.status === "live"), [flags]);

  const setStatus = async (flag: FeatureFlag, status: FeatureFlag["status"]) => {
    const payload: any = { status };
    if (status === "live") {
      payload.released_at = new Date().toISOString();
      payload.released_by = user?.id ?? null;
    }
    const { error } = await supabase.from("feature_flags").update(payload).eq("id", flag.id);
    if (error) {
      toast({ title: "Failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: status === "live" ? "Feature live" : status === "rolled_back" ? "Rolled back" : "Updated" });
    clearFeatureFlagCache();
    load();
  };

  const routeForRole = (role: string, ptaId: string): string => {
    switch (role) {
      case "Parent": return `/portal/${ptaId}`;
      case "Chair": return "/dashboard/chair";
      case "Treasurer": return "/dashboard/treasurer";
      case "Secretary": return "/dashboard/secretary";
      case "Trustee": return "/dashboard/trustee";
      case "Catering": return "/dashboard/catering";
      case "Communications": return "/dashboard/communications";
      case "School Liaison": return "/dashboard/school-liaison";
      case "Assets": return "/dashboard/assets";
      case "Community": return "/dashboard/community";
      case "Shop": return "/dashboard/shop";
      case "Events": return "/dashboard/events";
      default: return "/pta-admin";
    }
  };

  const beginPreview = (flag: FeatureFlag) => {
    if (!testPta) {
      toast({ title: "Test PTA not found", variant: "destructive" });
      return;
    }
    startPreview({
      feature_key: flag.key,
      feature_name: flag.name,
      role: previewRole,
      pta_id: testPta.id,
      pta_name: testPta.name,
    });
    const dest = routeForRole(previewRole, testPta.id);
    toast({ title: "Preview started", description: `Opening ${previewRole} dashboard in a new tab.` });
    setPreviewFor(null);
    setTimeout(() => window.open(dest, "_blank"), 50);
  };

  const openQuickLink = (link: QuickLink) => {
    if (!testPta) {
      toast({ title: "Test PTA not found", variant: "destructive" });
      return;
    }
    startPreview({
      feature_key: "*",
      feature_name: "All sandbox features",
      role: link.role,
      pta_id: testPta.id,
      pta_name: testPta.name,
    });
    // Small delay so sessionStorage is committed before the new tab reads it
    setTimeout(() => window.open(link.build(testPta.id), "_blank"), 50);
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const exportFlags = () => {
    if (flags.length === 0) {
      toast({ title: "Nothing to export", description: "No feature flags exist yet." });
      return;
    }
    const payload = {
      exported_at: new Date().toISOString(),
      flags: flags.map((f) => ({
        key: f.key,
        name: f.name,
        description: f.description,
        target_area: f.target_area,
        status: f.status,
        enabled_for_roles: f.enabled_for_roles,
        enabled_for_plans: f.enabled_for_plans,
        released_at: f.released_at,
        notes: f.notes,
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `feature-flags-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: "Exported", description: `${flags.length} flag(s) downloaded.` });
  };
  const exportFlag = (flag: FeatureFlag) => {
    const payload = {
      exported_at: new Date().toISOString(),
      flags: [{
        key: flag.key,
        name: flag.name,
        description: flag.description,
        target_area: flag.target_area,
        status: flag.status,
        enabled_for_roles: flag.enabled_for_roles,
        enabled_for_plans: flag.enabled_for_plans,
        released_at: flag.released_at,
        notes: flag.notes,
      }],
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `feature-flag-${flag.key}-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: "Exported", description: `${flag.name} downloaded.` });
  };


  const importFlags = async (file: File) => {
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const rows = Array.isArray(parsed?.flags) ? parsed.flags : null;
      if (!rows || rows.length === 0) {
        toast({ title: "Invalid file", description: "No flags found in JSON.", variant: "destructive" });
        return;
      }
      const cleaned = rows.map((r: any) => ({
        key: r.key,
        name: r.name,
        description: r.description ?? null,
        target_area: r.target_area ?? null,
        status: r.status ?? "sandbox",
        enabled_for_roles: r.enabled_for_roles ?? [],
        enabled_for_plans: r.enabled_for_plans ?? [],
        released_at: r.released_at ?? null,
        notes: r.notes ?? null,
      }));
      const { error } = await supabase.from("feature_flags").upsert(cleaned, { onConflict: "key" });
      if (error) {
        toast({ title: "Import failed", description: error.message, variant: "destructive" });
        return;
      }
      toast({ title: "Imported", description: `${cleaned.length} flag(s) added/updated.` });
      clearFeatureFlagCache();
      load();
    } catch (e: any) {
      toast({ title: "Import failed", description: e.message ?? "Could not parse file", variant: "destructive" });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };


  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FlaskConical className="h-5 w-5 text-amber-600" /> Sandbox
              </CardTitle>
              <CardDescription className="mt-1">
                Build new features against the Test PTA before exposing them to real users. Real PTAs are never affected.
              </CardDescription>
            </div>
            <div className="flex gap-2 shrink-0">
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) importFlags(f);
                }}
              />
              <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-4 w-4 mr-1" /> Import
              </Button>
              <Button size="sm" variant="outline" onClick={exportFlags}>
                <Download className="h-4 w-4 mr-1" /> Export
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>


      <Tabs defaultValue="in-dev" className="w-full">
        <TabsList>
          <TabsTrigger value="in-dev">In Development ({sandboxFlags.length})</TabsTrigger>
          <TabsTrigger value="published">Published ({liveFlags.length})</TabsTrigger>
          <TabsTrigger value="test-pta">Test PTA</TabsTrigger>
        </TabsList>

        <TabsContent value="in-dev" className="space-y-3 pt-4">
          <p className="text-sm text-muted-foreground">
            Flags are seeded into Test when a feature is built. To roll the same flag out to Live, click <strong>Export</strong> here, switch to Live, then click <strong>Import</strong> and upload the file.
          </p>

          {sandboxFlags.length === 0 && (
            <Card><CardContent className="py-6 text-center text-muted-foreground">No features in development.</CardContent></Card>
          )}
          {sandboxFlags.map((flag) => (
            <Card key={flag.id}>
              <CardContent className="p-4 flex flex-col md:flex-row gap-3 md:items-center justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{flag.name}</span>
                    <Badge variant="outline" className="font-mono text-xs">{flag.key}</Badge>
                    {flag.target_area && <Badge variant="secondary" className="text-xs">{flag.target_area}</Badge>}
                    <Badge className={flag.status === "rolled_back" ? "bg-destructive" : "bg-amber-500"}>{flag.status}</Badge>
                  </div>
                  {flag.description && <p className="text-sm text-muted-foreground mt-1">{flag.description}</p>}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => { setPreviewFor(flag); setPreviewRole("Chair"); }}>
                    <Eye className="h-4 w-4 mr-1" /> Preview
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => exportFlag(flag)}>
                    <Download className="h-4 w-4 mr-1" /> Export
                  </Button>
                  <Button size="sm" onClick={() => setStatus(flag, "live")}>
                    <Rocket className="h-4 w-4 mr-1" /> Go live
                  </Button>
                </div>

              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="published" className="space-y-3 pt-4">
          {liveFlags.length === 0 && (
            <Card><CardContent className="py-6 text-center text-muted-foreground">No live features yet.</CardContent></Card>
          )}
          {liveFlags.map((flag) => (
            <Card key={flag.id}>
              <CardContent className="p-4 flex flex-col md:flex-row gap-3 md:items-center justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{flag.name}</span>
                    <Badge variant="outline" className="font-mono text-xs">{flag.key}</Badge>
                    <Badge className="bg-emerald-600">live</Badge>
                    {flag.released_at && <span className="text-xs text-muted-foreground">Released {new Date(flag.released_at).toLocaleDateString()}</span>}
                  </div>
                  {flag.description && <p className="text-sm text-muted-foreground mt-1">{flag.description}</p>}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => exportFlag(flag)}>
                    <Download className="h-4 w-4 mr-1" /> Export
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setStatus(flag, "rolled_back")}>
                    <RotateCcw className="h-4 w-4 mr-1" /> Roll back
                  </Button>
                </div>

              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="test-pta" className="space-y-4 pt-4">
          {preview && (
            <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/30">
              <CardContent className="py-3 flex items-center justify-between gap-3">
                <div className="text-sm">
                  <span className="font-semibold">Active preview:</span>{" "}
                  <span>{preview.role}</span> @ <span>{preview.pta_name}</span>
                  {preview.feature_key !== "*" && (
                    <> — <span className="font-mono text-xs">{preview.feature_key}</span></>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={exitPreview}>
                  <X className="h-4 w-4 mr-1" /> Stop preview
                </Button>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle>{testPta?.name ?? "Test PTA not seeded"}</CardTitle>
              <CardDescription>
                A dedicated sandbox PTA. Excluded from public lists, search, billing, and trial expiry.
                Quick links open in a new tab with the matching role preview pre-activated.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {testPta && (
                <>
                  <div>
                    <Label className="text-xs text-muted-foreground">PTA ID</Label>
                    <p className="font-mono text-xs">{testPta.id}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm mb-2">Quick links</h4>
                    <div className="flex flex-wrap gap-2">
                      {TEST_PTA_QUICK_LINKS.map((l) => (
                        <Button key={l.label} size="sm" variant="outline" onClick={() => openQuickLink(l)}>
                          {l.label}
                          <Badge variant="secondary" className="ml-2 text-[10px]">{l.role}</Badge>
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm mb-2">Test members ({testMembers.length})</h4>
                    <div className="grid gap-1 text-sm">
                      {testMembers.map((m) => (
                        <div key={m.id} className="flex justify-between border rounded px-3 py-1.5">
                          <span>{m.full_name}</span>
                          <span className="text-muted-foreground">{m.role}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {testParent && (
                    <div>
                      <h4 className="font-semibold text-sm mb-2">Test parent</h4>
                      <div className="flex justify-between border rounded px-3 py-1.5 text-sm">
                        <span>{testParent.full_name}</span>
                        <span className="text-muted-foreground">{testParent.email}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!previewFor} onOpenChange={(o) => !o && setPreviewFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Preview "{previewFor?.name}"</DialogTitle>
            <DialogDescription>
              Choose which role's perspective you want to simulate. The feature will be enabled just for you, against the Test PTA, and the matching dashboard will open in a new tab.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Role</Label>
            <Select value={previewRole} onValueChange={setPreviewRole}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewFor(null)}>Cancel</Button>
            <Button onClick={() => previewFor && beginPreview(previewFor)}>Start preview</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
